import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { CustomSelect, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton } from '@redpanda/ui';
import { toIntlLocale } from '@redpanda/utils';
import { checkIn, checkOut } from '../services/api';
import { useEmployeeOptionsStore, employeeOptions } from '../stores/employeeStore';
import { useAttendanceStoreBase } from '../stores/attendanceStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';

interface QuickCheckInDialogProps {
  onClose: () => void;
}

type Action = 'checkIn' | 'checkOut';

/** 快速打卡：時間由伺服器決定，要等回應（今天已打卡等）才關（§3-17 等待型） */
const QuickCheckInDialog: React.FC<QuickCheckInDialogProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation(['pages', 'translation']);
  const { employees, fetchAll } = useEmployeeOptionsStore();
  const [employeeId, setEmployeeId] = useState('');
  const [action, setAction] = useState<Action>('checkIn');
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // 下拉選單：有快取先顯示、背景更新；時間每 30 秒更新
  useEffect(() => {
    fetchAll();
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, [fetchAll]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!employeeId) return;
    setLoading(true);
    try {
      const { data } = await (action === 'checkIn' ? checkIn(employeeId) : checkOut(employeeId));
      const store = useAttendanceStoreBase.getState();
      if (action === 'checkIn') store.addItem(data);
      else store.updateItem(data.id, data);
      toast.success(t(action === 'checkIn' ? 'pages:attendance.quickCheckIn.checkInSuccess' : 'pages:attendance.quickCheckIn.checkOutSuccess'));
      notifyDataChanged('attendance');
      onClose();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'translation:common.messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const actions: Action[] = ['checkIn', 'checkOut'];

  return (
    <DialogShell onClose={onClose} closeDisabled={loading} className="max-w-md">
      <DialogHeader title={t('pages:attendance.quickCheckIn.title')} onClose={onClose} closeDisabled={loading} closeLabel={t('translation:common.actions.close')} />
      <form onSubmit={submit}>
        <div className="p-6 space-y-5">
          <FormFieldWithError label={t('pages:attendance.form.employee')} required htmlFor="employeeId">
            <CustomSelect
              id="employeeId"
              value={employeeId}
              onChange={setEmployeeId}
              searchable
              placeholder={t('pages:attendance.form.selectEmployeePlaceholder')}
              options={employeeOptions(employees)}
            />
          </FormFieldWithError>

          {/* 上班／下班：分段切換（同 R24 檢視切換樣式） */}
          <div>
            <div className="inline-flex rounded-md border border-line p-0.5" role="radiogroup" aria-label={t('translation:common.table.columns.type')}>
              {actions.map(value => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={action === value}
                  onClick={() => setAction(value)}
                  className={`h-9 px-4 rounded text-sm font-medium transition-colors ${
                    action === value ? 'bg-accent-soft text-accent' : 'text-fg-secondary hover:text-fg'
                  }`}
                >
                  {t(`pages:attendance.quickCheckIn.${value}`)}
                </button>
              ))}
            </div>
            <p className="mt-3 text-sm text-fg-secondary tabular-nums">
              {t('pages:attendance.quickCheckIn.time', {
                time: now.toLocaleString(toIntlLocale(i18n.language), { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }),
              })}
            </p>
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={loading} className={dialogButtonClass.cancel}>
            {t('translation:common.actions.cancel')}
          </button>
          <LoadingButton type="submit" loading={loading} disabled={!employeeId} className={dialogButtonClass.primary}>
            {t(`pages:attendance.quickCheckIn.${action}`)}
          </LoadingButton>
        </DialogFooter>
      </form>
    </DialogShell>
  );
};

export default QuickCheckInDialog;
